import { toDateOnly } from './taskWorkflow';
import {
  DocumentStatus,
  FollowUpStatus,
  LINK_MESSAGE,
  effectiveFollowUpStatus,
  formatDay,
  isHttpLink,
  localToday
} from './legalCase';

export const NEXT_DATE_MESSAGE = 'Next date must be after the hearing date';
export const SUBMITTED_ON_MESSAGE = 'Submitted on is required when a document is Submitted';

const yesNo = (value) => (value === true ? 'Yes' : value === false ? 'No' : null);
const joined = (...parts) => parts.filter(Boolean).join(' · ');
const byDateDesc = (key) => (a, b) =>
  String(toDateOnly(b[key])).localeCompare(String(toDateOnly(a[key]))) || b.id - a.id;

// Same cap as the app's other upload fields (FormInput's MAX_FILE_SIZE).
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
export const UPLOAD_TOO_LARGE_MESSAGE = 'Files must be 20 MB or smaller';

/**
 * The name a court-case upload is stored under. /s3/upload keys every file by
 * its own name in one shared bucket, so a second "ledger.pdf" from anywhere in
 * the app would overwrite the first; the case id and a timestamp keep it
 * unique, and the name stays URL-safe.
 */
export function storageFileName(taskId, fileName, now = Date.now()) {
  const name = String(fileName || '');
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot).toLowerCase() : '';
  const safe = (text) => text.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
  return `legal-case-${taskId}-${now}-${safe(base) || 'file'}${safe(ext)}`;
}

/**
 * Hearings, follow-ups and documents on a court case — one entry per record
 * kind, as the backend's ChildKind. `path` is the URL segment under
 * /task/:id/legal; `listKey` is the array on `task.legalCase`.
 *
 * Field types: text, textarea, date, dropdown (values from legal-options,
 * named by `optionsKey`), yesno (not recorded / Yes / No — the API keeps "not
 * recorded" apart from No), checkbox, and file (uploaded to S3 on Save; the
 * field holds the file's URL).
 */
export const LEGAL_RECORDS = {
  hearing: {
    title: 'Hearings',
    noun: 'Hearing',
    path: 'hearings',
    listKey: 'hearings',
    empty: 'No hearings recorded yet.',
    hint: 'Record a hearing with a next date and the next hearing is added for you.',
    nameOf: (row) => `Hearing on ${formatDay(row.hearingDate)}`,
    sort: byDateDesc('hearingDate'),
    validate: (values) => {
      const hearing = toDateOnly(values.hearingDate);
      const next = toDateOnly(values.nextDate);
      return hearing && next && next <= hearing ? NEXT_DATE_MESSAGE : null;
    },
    fields: [
      { key: 'hearingDate', label: 'Hearing date', type: 'date', required: true },
      { key: 'hearingTime', label: 'Time', type: 'text', placeholder: '14:30' },
      { key: 'purpose', label: 'Purpose', type: 'text', wide: true },
      { key: 'court', label: 'Court', type: 'text', placeholder: 'Defaults to the case court' },
      { key: 'advocate', label: 'Advocate', type: 'text', placeholder: 'Defaults to the case advocate' },
      { key: 'preparationRequired', label: 'Preparation required', type: 'textarea', wide: true },
      { key: 'documentsReady', label: 'Documents ready?', type: 'yesno' },
      { key: 'attended', label: 'Attended?', type: 'yesno' },
      { key: 'outcome', label: 'Outcome', type: 'textarea', wide: true },
      { key: 'nextDate', label: 'Next date', type: 'date' },
      { key: 'notes', label: 'Notes', type: 'textarea', wide: true }
    ],
    columns: [
      {
        label: 'Date',
        render: (row) => joined(formatDay(row.hearingDate), row.hearingTime)
      },
      { label: 'Purpose', render: (row) => row.purpose },
      { label: 'Court / Advocate', render: (row) => joined(row.court, row.advocate) },
      { label: 'Docs ready', render: (row) => yesNo(row.documentsReady) },
      { label: 'Attended', render: (row) => yesNo(row.attended) },
      { label: 'Outcome', render: (row) => row.outcome },
      { label: 'Next date', render: (row) => (row.nextDate ? formatDay(row.nextDate) : null) }
    ]
  },

  // The case's task owner follows these up, so a follow-up carries no owner of
  // its own (the API's ownerParty / ownerUserId are left unset).
  followUp: {
    title: 'Follow-ups',
    noun: 'Follow-up',
    path: 'follow-ups',
    listKey: 'followUps',
    empty: 'No follow-ups logged yet.',
    hint: 'A Pending follow-up past its due date shows as Delayed. One that asks for escalation escalates the case.',
    nameOf: (row) => `Follow-up on ${formatDay(row.followUpDate)}`,
    sort: byDateDesc('followUpDate'),
    defaults: () => ({ followUpDate: localToday(), status: FollowUpStatus.PENDING }),
    fields: [
      { key: 'followUpDate', label: 'Date', type: 'date', required: true },
      { key: 'followUpWith', label: 'With whom', type: 'text' },
      { key: 'mode', label: 'Mode', type: 'dropdown', optionsKey: 'followUpModes' },
      { key: 'status', label: 'Status', type: 'dropdown', optionsKey: 'followUpStatuses', required: true },
      { key: 'summary', label: 'Summary / discussion', type: 'textarea', required: true, wide: true },
      { key: 'commitment', label: 'Commitment', type: 'text' },
      { key: 'nextAction', label: 'Next action', type: 'text' },
      { key: 'dueDate', label: 'Due date', type: 'date' },
      { key: 'escalationRequired', label: 'Escalation required', type: 'checkbox' }
    ],
    columns: [
      { label: 'Date', render: (row) => formatDay(row.followUpDate) },
      { label: 'With', render: (row) => joined(row.followUpWith, row.mode) },
      { label: 'Summary', render: (row) => row.summary },
      {
        label: 'Commitment',
        render: (row) => joined(row.commitment, row.nextAction && `Next: ${row.nextAction}`)
      },
      { label: 'Due', render: (row) => (row.dueDate ? formatDay(row.dueDate) : null) },
      {
        label: 'Status',
        render: (row, ctx) =>
          joined(effectiveFollowUpStatus(row, ctx.today), row.escalationRequired && 'Escalate')
      }
    ]
  },

  document: {
    title: 'Case documents',
    noun: 'Document',
    path: 'documents',
    listKey: 'documents',
    empty: 'No documents registered yet.',
    hint: 'Required, Missing, Original Pending and Soft Copy Pending count as documents pending.',
    nameOf: (row) => joined(row.docCode, row.name),
    sort: (a, b) => String(a.docCode || '').localeCompare(String(b.docCode || '')) || a.id - b.id,
    defaults: () => ({ status: DocumentStatus.REQUIRED }),
    validate: (values) => {
      if (values.status === DocumentStatus.SUBMITTED && !toDateOnly(values.submittedOn)) {
        return SUBMITTED_ON_MESSAGE;
      }
      return isHttpLink(String(values.link || '').trim()) ? null : LINK_MESSAGE;
    },
    fields: [
      { key: 'name', label: 'Document name', type: 'text', required: true },
      { key: 'docType', label: 'Type', type: 'text' },
      { key: 'status', label: 'Status', type: 'dropdown', optionsKey: 'documentStatuses', required: true },
      { key: 'neededBy', label: 'Needed by', type: 'date' },
      {
        key: 'submittedOn',
        label: 'Submitted on',
        type: 'date',
        requiredIf: (values) => values.status === DocumentStatus.SUBMITTED
      },
      { key: 'originalWith', label: 'Original with', type: 'text' },
      { key: 'storedLocation', label: 'Stored at', type: 'text' },
      { key: 'fileUrl', label: 'Upload file', type: 'file', wide: true },
      { key: 'link', label: 'Link', type: 'text', placeholder: 'https://', wide: true },
      { key: 'remarks', label: 'Remarks', type: 'textarea', wide: true }
    ],
    columns: [
      { label: 'ID', render: (row) => row.docCode },
      { label: 'Document', render: (row) => joined(row.name, row.docType) },
      { label: 'Status', render: (row) => row.status },
      { label: 'Needed by', render: (row) => (row.neededBy ? formatDay(row.neededBy) : null) },
      { label: 'Submitted on', render: (row) => (row.submittedOn ? formatDay(row.submittedOn) : null) },
      { label: 'Original / stored', render: (row) => joined(row.originalWith, row.storedLocation) },
      { label: 'File / link', type: 'links' }
    ]
  }
};

/** Form values for a record: the stored row, or the kind's defaults. */
export function recordFormValues(config, row) {
  const source = row || config.defaults?.() || {};
  const values = {};
  for (const field of config.fields) {
    const value = source[field.key];
    if (field.type === 'yesno') {
      values[field.key] = value === true ? 'true' : value === false ? 'false' : '';
    } else if (field.type === 'checkbox') {
      values[field.key] = value === true;
    } else if (field.type === 'date') {
      values[field.key] = toDateOnly(value);
    } else {
      values[field.key] = value ?? '';
    }
  }
  return values;
}

/**
 * The request body. A new record leaves blanks out so the API's defaults
 * apply (a hearing's court and advocate come from the case); an edit sends
 * them as null so a field can be cleared.
 */
export function recordPayload(config, values, { isNew }) {
  const payload = {};
  for (const field of config.fields) {
    let value;
    if (field.type === 'yesno') {
      value = values[field.key] === 'true' ? true : values[field.key] === 'false' ? false : null;
    } else if (field.type === 'checkbox') {
      value = values[field.key] === true;
    } else {
      const text = String(values[field.key] ?? '').trim();
      value = text ? text : null;
    }
    if (isNew && value === null) continue;
    payload[field.key] = value;
  }
  return payload;
}
